import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Chip,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
} from '@mui/material';
import {
  Event as EventIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { Agent, TypeAppointment, AppointmentSettings, TrinksConfig, SchedulingFlow } from '../../types/agent';
import { agentApi } from '../../utils/agentApi';
import ProfessionalCadastrosDialog from './ProfessionalCadastrosDialog';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';

interface AgendamentoCardProps {
  agent: Agent;
  onAgentUpdate?: (agent: Agent) => void;
}

const AgendamentoCard: React.FC<AgendamentoCardProps> = ({ agent, onAgentUpdate }) => {
  const { t } = useTranslation();
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [cadastrosDialogOpen, setCadastrosDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [typeAppointment, setTypeAppointment] = useState<TypeAppointment>(agent?.typeAppointment ?? 'Trinks');
  const [schedulingUrl, setSchedulingUrl] = useState(agent?.appointmentSettings?.schedulingUrl ?? '');
  const [schedulingFlow, setSchedulingFlow] = useState<SchedulingFlow>(
    agent?.appointmentSettings?.schedulingFlow ?? 'Simples'
  );
  const [trinksConfig, setTrinksConfig] = useState<TrinksConfig>({
    baseUrl: agent?.appointmentSettings?.trinks?.baseUrl ?? '',
    apiKey: agent?.appointmentSettings?.trinks?.apiKey ?? '',
    companyId: agent?.appointmentSettings?.trinks?.companyId ?? '',
  });
  const theme = useTheme();

  const enabled = agent?.appointmentSettings != null;

  const handleToggle = async (checked: boolean) => {
    setIsSaving(true);
    try {
      const appointmentSettings: AppointmentSettings | null = checked
        ? {
            schedulingUrl: agent?.appointmentSettings?.schedulingUrl ?? '',
            schedulingFlow: agent?.appointmentSettings?.schedulingFlow ?? 'Simples',
            trinks: { baseUrl: '', apiKey: '', companyId: '' },
          }
        : null;
      const typeAppt: TypeAppointment = checked ? 'Trinks' : (agent?.typeAppointment ?? 'Trinks');

      const updatedAgent = await agentApi.updateAgent(agent.id, {
        ...agent,
        typeAppointment: typeAppt,
        appointmentSettings,
      });

      if (onAgentUpdate) {
        onAgentUpdate(updatedAgent);
      }
      toast.success(checked ? t('agents.schedulingSettings.enabledSuccess') : t('agents.schedulingSettings.disabledSuccess'));
    } catch (error) {
      console.error('Error toggling agendamento:', error);
      toast.error(t('agents.schedulingSettings.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfigSave = async () => {
    setIsSaving(true);
    try {
      const appointmentSettings: AppointmentSettings = {
        schedulingUrl: schedulingUrl.trim() || undefined,
        schedulingFlow: schedulingFlow,
      };
      if (typeAppointment === 'Trinks') {
        appointmentSettings.trinks = trinksConfig;
      }

      const updatedAgent = await agentApi.updateAgent(agent.id, {
        ...agent,
        typeAppointment,
        appointmentSettings,
      });

      if (onAgentUpdate) {
        onAgentUpdate(updatedAgent);
      }
      toast.success(t('agents.schedulingSettings.saveSuccess'));
      setConfigDialogOpen(false);
    } catch (error) {
      console.error('Error saving scheduling settings:', error);
      toast.error(t('agents.schedulingSettings.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenConfig = () => {
    setSchedulingUrl(agent?.appointmentSettings?.schedulingUrl ?? '');
    setSchedulingFlow(agent?.appointmentSettings?.schedulingFlow ?? 'Simples');
    setTypeAppointment(agent?.typeAppointment ?? 'Trinks');
    setTrinksConfig({
      baseUrl: agent?.appointmentSettings?.trinks?.baseUrl ?? '',
      apiKey: agent?.appointmentSettings?.trinks?.apiKey ?? '',
      companyId: agent?.appointmentSettings?.trinks?.companyId ?? '',
    });
    setConfigDialogOpen(true);
  };

  const getStatusInfo = () => {
    if (!enabled) {
      return {
        color: 'default' as const,
        icon: <InfoIcon />,
        text: t('agents.schedulingSettings.disabled'),
        description: t('agents.schedulingSettings.notConfigured'),
      };
    }

    const hasTrinksConfig = agent?.appointmentSettings?.trinks?.baseUrl;
    const agentType = agent?.typeAppointment ?? 'Trinks';
    if (agentType === 'Trinks' && !hasTrinksConfig) {
      return {
        color: 'warning' as const,
        icon: <ErrorIcon />,
        text: t('agents.schedulingSettings.incomplete'),
        description: t('agents.schedulingSettings.configureToUse'),
      };
    }

    return {
      color: 'success' as const,
      icon: <CheckCircleIcon />,
      text: t('agents.schedulingSettings.active'),
      description: t(`agents.schedulingSettings.typeLabel.${agentType.toLowerCase()}`),
    };
  };

  const statusInfo = getStatusInfo();

  return (
    <>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EventIcon color="primary" />
              <Typography variant="h6">{t('agents.schedulingSettings.title')}</Typography>
            </Box>
            <Chip
              icon={statusInfo.icon}
              label={statusInfo.text}
              color={statusInfo.color}
              variant="outlined"
            />
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {statusInfo.description}
          </Typography>

          {enabled && agent?.appointmentSettings?.trinks?.baseUrl && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>{t('agents.schedulingSettings.trinks')}</strong> {agent.appointmentSettings.trinks.baseUrl}
              </Typography>
            </Box>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(e) => handleToggle(e.target.checked)}
                color="primary"
                disabled={isSaving}
              />
            }
            label={t('agents.schedulingSettings.enable')}
          />
        </CardContent>

        <CardActions sx={{ px: 2, pb: 2 }}>
          <Button
            variant="outlined"
            startIcon={isSaving ? <CircularProgress size={16} /> : <SettingsIcon />}
            onClick={handleOpenConfig}
            disabled={!enabled || isSaving}
          >
            {isSaving ? t('agents.schedulingSettings.saving') : t('agents.schedulingSettings.configure')}
          </Button>
          {enabled && (
            <Button
              variant="outlined"
              startIcon={<PeopleIcon />}
              onClick={() => setCadastrosDialogOpen(true)}
              disabled={isSaving}
            >
              {t('agents.cadastros.button')}
            </Button>
          )}
        </CardActions>
      </Card>

      <Dialog open={configDialogOpen} onClose={() => setConfigDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('agents.schedulingSettings.title')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label={t('agents.schedulingSettings.schedulingUrl')}
              value={schedulingUrl}
              onChange={(e) => setSchedulingUrl(e.target.value)}
              fullWidth
              placeholder={t('agents.schedulingSettings.schedulingUrlPlaceholder')}
            />
            <FormControl fullWidth>
              <InputLabel>{t('agents.schedulingSettings.schedulingFlow')}</InputLabel>
              <Select
                label={t('agents.schedulingSettings.schedulingFlow')}
                value={schedulingFlow}
                onChange={(e) => setSchedulingFlow(e.target.value as SchedulingFlow)}
              >
                <MenuItem value="Simples">{t('agents.schedulingSettings.schedulingFlowSimples')}</MenuItem>
                <MenuItem value="Intermediario">{t('agents.schedulingSettings.schedulingFlowIntermediario')}</MenuItem>
                <MenuItem value="Avancado">{t('agents.schedulingSettings.schedulingFlowAvancado')}</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>{t('agents.schedulingSettings.typeAppointment')}</InputLabel>
              <Select
                label={t('agents.schedulingSettings.typeAppointment')}
                value={typeAppointment}
                onChange={(e) => setTypeAppointment(e.target.value as TypeAppointment)}
              >
                <MenuItem value="Trinks">{t('agents.schedulingSettings.trinks')}</MenuItem>
                <MenuItem value="GoogleCalendar">{t('agents.schedulingSettings.googleCalendar')}</MenuItem>
                <MenuItem value="LiaCalendar">{t('agents.schedulingSettings.liaCalendar')}</MenuItem>
              </Select>
            </FormControl>

            {typeAppointment === 'Trinks' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label={t('agents.schedulingSettings.trinksBaseUrl')}
                  value={trinksConfig.baseUrl ?? ''}
                  onChange={(e) => setTrinksConfig((prev) => ({ ...prev, baseUrl: e.target.value }))}
                  fullWidth
                  placeholder="https://api.trinks.com"
                />
                <TextField
                  label={t('agents.schedulingSettings.trinksApiKey')}
                  value={trinksConfig.apiKey ?? ''}
                  onChange={(e) => setTrinksConfig((prev) => ({ ...prev, apiKey: e.target.value }))}
                  fullWidth
                  type="password"
                  autoComplete="off"
                />
                <TextField
                  label={t('agents.schedulingSettings.trinksCompanyId')}
                  value={trinksConfig.companyId ?? ''}
                  onChange={(e) => setTrinksConfig((prev) => ({ ...prev, companyId: e.target.value }))}
                  fullWidth
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }} onClick={handleConfigSave} disabled={isSaving}>
            {isSaving ? <CircularProgress size={20} /> : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <ProfessionalCadastrosDialog
        open={cadastrosDialogOpen}
        onClose={() => setCadastrosDialogOpen(false)}
        agentId={agent.id}
        isTrinks={agent?.typeAppointment === 'Trinks'}
      />
    </>
  );
};

export default AgendamentoCard;
